import { TallyEntry } from '../../types/tally';

const convertToTallyDateFormat = (dateString: string): string => {
  if (!dateString || dateString.trim() === "") return "";
 
  try {
    const parts = dateString.split('-');
    if (parts.length === 3) {
      const day = parts[0];
      const month = parts[1];
      const year = parts[2];
      return `${day}-${month}-${year}`;
    }
    return dateString;
  } catch (error) {
    return dateString;
  }
};

const escapeXml = (unsafe: string = ""): string => {
  if (!unsafe) return "";
 
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
};

export const convertToXML = (tallyData: TallyEntry[]): string => {
  if (!tallyData || tallyData.length === 0) {
    throw new Error("No valid data to convert to XML");
  }
 
  const companyName = tallyData[0]?.customerName || "";
 
  let xml = `<ENVELOPE>
  <HEADER>
    <TALLYREQUEST>Import Data</TALLYREQUEST>
    <TYPE>Data</TYPE>
    <ID>Vouchers</ID>
  </HEADER>
  <BODY>
    <IMPORTDATA>
      <REQUESTDESC>
        <REPORTNAME>Vouchers</REPORTNAME>
        <STATICVARIABLES>
          <SVCURRENTCOMPANY>${escapeXml(companyName)}</SVCURRENTCOMPANY>
        </STATICVARIABLES>
      </REQUESTDESC>
      <REQUESTDATA>`;
 
  tallyData.forEach(entry => {
    const formattedVoucherDate = convertToTallyDateFormat(entry.voucherDate);
    const formattedInvoiceDate = convertToTallyDateFormat(entry.invoiceDate);
    const isSales = entry.voucherType.toLowerCase().includes('sales');
    const partyLedgerIsDeemedPositive = entry.partyLedgerType === 'Dr' ? 'Yes' : 'No';
    const partyLedgerAmount = entry.partyLedgerType === 'Dr' ? `-${entry.creditAmount.toFixed(2)}` : entry.creditAmount.toFixed(2);

    xml += `
        <TALLYMESSAGE xmlns:UDF="TallyUDF">
          <VOUCHER VCHTYPE="${escapeXml(entry.voucherType)}" ACTION="Create" OBJVIEW="Accounting Voucher View">
            <DATE>${formattedVoucherDate}</DATE>
            <REFERENCEDATE>${formattedInvoiceDate}</REFERENCEDATE>
            <VOUCHERTYPENAME>${escapeXml(entry.voucherType)}</VOUCHERTYPENAME>
            <VOUCHERNUMBER>${escapeXml(entry.voucherNumber)}</VOUCHERNUMBER>
            <REFERENCE>${escapeXml(entry.reference)}</REFERENCE>
            <NARRATION>${escapeXml(entry.narration)}</NARRATION>
            <PARTYLEDGERNAME>${escapeXml(entry.creditLedger)}</PARTYLEDGERNAME>`;
 
    xml += `
            <ALLLEDGERENTRIES.LIST>
              <LEDGERNAME>${escapeXml(entry.creditLedger)}</LEDGERNAME>
              <ISDEEMEDPOSITIVE>${partyLedgerIsDeemedPositive}</ISDEEMEDPOSITIVE>
              <AMOUNT>${partyLedgerAmount}</AMOUNT>
              <ISPARTYLEDGER>Yes</ISPARTYLEDGER>
            </ALLLEDGERENTRIES.LIST>`;
 
    // Process debit items for Purchase, or TDS for Sales
    entry.debitItems.forEach(item => {
      xml += `
            <ALLLEDGERENTRIES.LIST>
              <LEDGERNAME>${escapeXml(item.ledgerName)}</LEDGERNAME>
              <ISDEEMEDPOSITIVE>Yes</ISDEEMEDPOSITIVE>
              <AMOUNT>-${item.amount.toFixed(2)}</AMOUNT>`;
             
      if (item.isInventoryItem && item.inventoryAllocations && item.inventoryAllocations.length > 0) {
        item.inventoryAllocations.forEach(allocation => {
          xml += `
              <INVENTORYALLOCATIONS.LIST>
                <STOCKITEMNAME>${escapeXml(allocation.itemName)}</STOCKITEMNAME>
                <ISDEEMEDPOSITIVE>Yes</ISDEEMEDPOSITIVE>
                <AMOUNT>-${allocation.amount.toFixed(2)}</AMOUNT>
                <ACTUALQTY>${allocation.quantity}</ACTUALQTY>
                <BILLEDQTY>${allocation.quantity}</BILLEDQTY>
                <RATE>${allocation.rate.toFixed(2)}</RATE>
                <UNITS>${escapeXml(allocation.unit)}</UNITS>
              </INVENTORYALLOCATIONS.LIST>`;
        });
      }
     
      xml += `
            </ALLLEDGERENTRIES.LIST>`;
    });
 
    if (entry.tdsLedger && entry.tdsAmount && entry.tdsAmount > 0) {
        const isDeemedPositive = isSales ? 'Yes' : 'No';
        const amount = isSales ? `-${entry.tdsAmount.toFixed(2)}` : entry.tdsAmount.toFixed(2);
        xml += `
            <ALLLEDGERENTRIES.LIST>
              <LEDGERNAME>${escapeXml(entry.tdsLedger)}</LEDGERNAME>
              <ISDEEMEDPOSITIVE>${isDeemedPositive}</ISDEEMEDPOSITIVE>
              <AMOUNT>${amount}</AMOUNT>
            </ALLLEDGERENTRIES.LIST>`;
    }
 
    // Process credit items
    if (entry.creditItems && entry.creditItems.length > 0) {
      entry.creditItems.forEach((item: any) => {
        xml += `
            <ALLLEDGERENTRIES.LIST>
              <LEDGERNAME>${escapeXml(item.ledgerName)}</LEDGERNAME>
              <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>
              <AMOUNT>${item.amount.toFixed(2)}</AMOUNT>`;

        if (item.isInventoryItem && item.inventoryAllocations && item.inventoryAllocations.length > 0) {
            item.inventoryAllocations.forEach((allocation: any) => {
                xml += `
              <INVENTORYALLOCATIONS.LIST>
                <STOCKITEMNAME>${escapeXml(allocation.itemName)}</STOCKITEMNAME>
                <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>
                <AMOUNT>${allocation.amount.toFixed(2)}</AMOUNT>
                <ACTUALQTY>-${allocation.quantity}</ACTUALQTY>
                <BILLEDQTY>-${allocation.quantity}</BILLEDQTY>
                <RATE>${allocation.rate.toFixed(2)}</RATE>
                <UNITS>${escapeXml(allocation.unit)}</UNITS>
              </INVENTORYALLOCATIONS.LIST>`;
            });
        }

        xml += `
            </ALLLEDGERENTRIES.LIST>`;
      });
    }
 
    xml += `
          </VOUCHER>
        </TALLYMESSAGE>`;
  });
 
  xml += `
      </REQUESTDATA>
    </IMPORTDATA>
  </BODY>
</ENVELOPE>`;
 
  return xml;
};